import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { PlayIcon, PauseIcon, StopIcon } from "../../assests/icons/Icons";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Search, ArrowUp, ArrowDown, FolderOutput, MoreVertical, Users, Loader2 } from "lucide-react";
import UndoButton from "../session/UndoButton";
import AppButton from "./AppButton";
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js'
import { Input, Switch, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@heroui/react";

// Undo is built and wired (redux/undo) but held back from users for now.
// Not mounting the button also removes its Ctrl/Cmd+Z listener. Flip to
// true to ship it; nothing else needs to change.
const UNDO_ENABLED = false;



const formatTime = (seconds) => {
  if (!seconds) return "0:00";
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  return `${min}:${sec < 10 ? "0" : ""}${sec}`;
};

const AudioPlayer = ({
  filename,
  sessionId,
  currentTime,
  setCurrentTime,
  duration,
  setDuration,
  newTimeSetted,
  headings,
  wordOffsets,
  paragraphs,
  searchQuery,
  onSearchQueryChange,
  onSearchPrev,
  onSearchNext,
  searchPosition,
  onExportClick,
  autoscrollEnabled,
  onAutoscrollChange,
  onPlayingStateChange,
  isActive = true,
  promptMode = "soft", // "hard" or "soft"
  onPromptModeChange,
  onToggleSpeakersModal,
  highlights = [], // [{id, start_offset, end_offset, color}, ...]
  onHighlightClick,
}) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState(false);
  // 'peaks' while we're fetching waveform peaks, 'audio' once wavesurfer is
  // downloading the media itself. null when idle or ready. Drives the label
  // shown in the loading overlay so the user knows which phase is slow.
  const [loadPhase, setLoadPhase] = useState(null);
  // True when the user has seeked to a position that is not buffered yet
  // and we are waiting on the browser to fetch that range. Drives the same
  // overlay + play-button spinner as the initial load.
  const [isSeekBuffering, setIsSeekBuffering] = useState(false);
  const isSeeking = useRef(false);
  const manualScrub = useRef(false);
  const scrubStartX = useRef(0);
  const scrubStartTime = useRef(0);
  const SCRUB_SENSITIVITY = 0.22; // lower = finer control
  const [playbackRate, setPlaybackRate] = useState(1);
  const speedOptions = [0.75, 1, 1.25, 1.5, 2];
  const isMacOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  }, []);
  const modifierLabel = isMacOS ? "Option" : "Ctrl";
  const lastReportedTimeRef = useRef(null);
  const pendingRafRef = useRef(null);
  const queuedReportTimeRef = useRef(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchToggleRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const resumeWaitCleanupRef = useRef(null);
  const headingsRef = useRef(Array.isArray(headings) ? headings : []);
  const lastExternalJumpRef = useRef(null);
  const normalizedWordOffsets = useMemo(() => {
    if (!Array.isArray(wordOffsets) || wordOffsets.length === 0) return [];
    const sanitized = wordOffsets
      .map((value) => (Number.isFinite(value) ? value : null))
      .filter((value) => value != null)
      .sort((a, b) => a - b);
    const deduped = [];
    for (const value of sanitized) {
      if (deduped.length === 0 || Math.abs(deduped[deduped.length - 1] - value) > 0.01) {
        deduped.push(value);
      }
    }
    return deduped;
  }, [wordOffsets]);

  useEffect(() => {
    headingsRef.current = Array.isArray(headings) ? headings : [];
  }, [headings]);

  const reportCurrentTime = useCallback(
    (nextTime) => {
      if (!Number.isFinite(nextTime)) return;
      queuedReportTimeRef.current = nextTime;
      if (pendingRafRef.current != null) return;
      pendingRafRef.current = requestAnimationFrame(() => {
        pendingRafRef.current = null;
        const value = queuedReportTimeRef.current;
        queuedReportTimeRef.current = null;
        if (!Number.isFinite(value)) return;
        if (
          lastReportedTimeRef.current != null &&
          Math.abs(lastReportedTimeRef.current - value) < 0.005
        ) {
          return;
        }
        lastReportedTimeRef.current = value;
        setCurrentTime(value);
      });
    },
    [setCurrentTime]
  );

  const cycleSpeed = () => {
    const currentIndex = speedOptions.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speedOptions.length;
    const newRate = speedOptions[nextIndex];
    setPlaybackRate(newRate);
    wavesurferRef.current?.setPlaybackRate(newRate);
  };

  const stepSpeed = (direction) => {
    const currentIndex = speedOptions.indexOf(playbackRate);
    const safeIndex = currentIndex >= 0 ? currentIndex : speedOptions.indexOf(1);
    const nextIndex = Math.min(
      speedOptions.length - 1,
      Math.max(0, safeIndex + direction)
    );
    const newRate = speedOptions[nextIndex];
    if (newRate === playbackRate) return;
    setPlaybackRate(newRate);
    wavesurferRef.current?.setPlaybackRate(newRate);
  };

  async function fetchWaveformPeaks(signedUrl, {
    mode = "peak",
    format = "json",
    bins = 300,
    timeoutMs = 15000
  } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    // Same backend as everything else. This was a hard-coded production
    // host, so the dev frontend was generating waveforms on main — and a
    // URL outside REACT_APP_BACKEND_URL would also miss the API key header.
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}waveform_generator_000141_1_0_0/api/generate-waveform`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode, format, bins, signed_url: signedUrl }),
      signal: controller.signal,
    }).catch((e) => {
      clearTimeout(timer);
      throw e;
    });

    clearTimeout(timer);

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Waveform API ${res.status}: ${text || res.statusText}`);
    }
    console.log(res)
    return res.json(); // expect JSON with peaks (and maybe duration)
  }

  useEffect(() => {
    return () => {
      if (pendingRafRef.current != null) {
        cancelAnimationFrame(pendingRafRef.current);
        pendingRafRef.current = null;
      }
      queuedReportTimeRef.current = null;
      if (resumeWaitCleanupRef.current) {
        resumeWaitCleanupRef.current();
        resumeWaitCleanupRef.current = null;
      }
    };
  }, []);

  const clearPendingResumeWait = useCallback(() => {
    if (resumeWaitCleanupRef.current) {
      resumeWaitCleanupRef.current();
      resumeWaitCleanupRef.current = null;
    }
  }, []);

  const isBufferedAtTime = useCallback((mediaEl, timeSec) => {
    if (!mediaEl || !mediaEl.buffered) return true;
    const t = Number(timeSec);
    if (!Number.isFinite(t)) return true;
    const ranges = mediaEl.buffered;
    for (let i = 0; i < ranges.length; i += 1) {
      const start = ranges.start(i);
      const end = ranges.end(i);
      // Require a tiny forward margin to avoid immediate re-stall after resume
      if (t >= start - 0.05 && t + 0.12 <= end) {
        return true;
      }
    }
    return false;
  }, []);

  const rebuildRegions = useCallback((regionHeadings) => {
    const ws = wavesurferRef.current;
    const r = regionsRef.current;
    if (!ws || !r) return;
    const wsDuration = ws.getDuration();
    if (!Number.isFinite(wsDuration) || wsDuration <= 0) return;

    try {
      r.getRegions().forEach((reg) => reg.remove());
    } catch {}

    const color = (index) => (index % 2 === 1 ? "rgba(40, 71, 121, 0.5)" : "rgba(153, 182, 239, 0.5)");
    const hsSorted = (Array.isArray(regionHeadings) ? regionHeadings : [])
      .map((h) => ({ ...h, start: Number(h?.start) }))
      .filter((h) => Number.isFinite(h.start))
      .sort((a, b) => a.start - b.start);

    try {
      if (hsSorted.length > 0) {
        r.addRegion({
          start: 0,
          end: Math.max(hsSorted[0].start - 1, 0),
          color: color(1),
          resize: false,
          drag: false,
        });
      }

      for (let i = 0; i < hsSorted.length - 1; i += 1) {
        const cur = hsSorted[i];
        const next = hsSorted[i + 1];
        r.addRegion({
          start: cur.start,
          end: Math.max(next.start - 1, cur.start),
          color: color(i),
          resize: false,
          drag: false,
        });
      }

      const last = hsSorted[hsSorted.length - 1];
      if (last) {
        r.addRegion({
          start: last.start,
          end: wsDuration,
          color: color(hsSorted.length - 1),
          resize: false,
          drag: false,
        });
      } else {
        r.addRegion({
          start: 0,
          end: wsDuration,
          color: color(1),
          resize: false,
          drag: false,
        });
      }
    } catch (error) {
      console.error("Failed to rebuild waveform regions:", error);
    }
  }, []);
  useEffect(() => {
    if (!isSearchOpen) return;
    const handleClick = (event) => {
      const dropdownEl = searchDropdownRef.current;
      const toggleEl = searchToggleRef.current;
      if (
        dropdownEl &&
        !dropdownEl.contains(event.target) &&
        toggleEl &&
        !toggleEl.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!waveformRef.current || !filename) return;
    lastReportedTimeRef.current = null;
    
    // Ensure the container element is properly mounted
    if (!waveformRef.current.parentNode) {
      console.error("Waveform container not properly mounted");
      return;
    }

    const fetchAndLoadBlob = async () => {
      try {
        setDownloadProgress(0);
        setIsAudioReady(false);
        setAudioLoadError(false);
        setLoadPhase("peaks");

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}transcriptions_poc_000113_1_0_0/transcription/get-url-mp3/${filename}`
        );
        const data = await response.json();
        if (!data.url) throw new Error("No URL returned from backend");

        // const blobResponse = await fetch(data.url);
        // const reader = blobResponse.body?.getReader();
        // const contentLength = +blobResponse.headers.get("Content-Length") || 0;

        // const chunks = [];
        // let received = 0;

        // while (true) {
        //   const { done, value } = await reader.read();
        //   if (done) break;
        //   chunks.push(value);
        //   received += value.length;
        //   setDownloadProgress(Math.round((received / contentLength) * 100));
        // }

        // const blob = new Blob(chunks);
        

        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
        }

        const regions = RegionsPlugin.create()
        regionsRef.current = regions;

        try {
          wavesurferRef.current = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: "rgb(90 100 123)",
            progressColor: "rgb(216 226 253)",
            cursorColor: "#960010",
            barWidth: 3,
            height: 50,
            normalize: true,
            // we implement a custom, finer-grained scrub behavior below
            dragToSeek: false,
            playbackRate: playbackRate,
            partialRender: true,
            plugins: [regions],
          });
        } catch (error) {
          console.error("Error creating WaveSurfer instance:", error);
          return;
        }
        
        
        const fakePeaks = Array.from({ length: 300 }, () => Math.random());
        
        try{
          const response2 = await fetchWaveformPeaks(data.url)
          const peaks = response2["peaks"];
          
          // Validate peaks data
          if (peaks && Array.isArray(peaks) && peaks.length > 0 && peaks.length < 100000) {
            try {
              wavesurferRef.current.load(data.url, peaks);
            } catch (loadError) {
              console.error("Error loading audio with peaks:", loadError);
              wavesurferRef.current.load(data.url, fakePeaks);
            }
          } else {
            console.warn("Invalid peaks data, using fake peaks");
            try {
              wavesurferRef.current.load(data.url, fakePeaks);
            } catch (loadError) {
              console.error("Error loading audio with fake peaks:", loadError);
              wavesurferRef.current.load(data.url);
            }
          }

        }catch (err) {
          console.error("❌ Error loading audio:", err);
          try {
            wavesurferRef.current.load(data.url, fakePeaks);
          } catch (loadError) {
            console.error("Error loading audio with fake peaks:", loadError);
            wavesurferRef.current.load(data.url);
          }
        }
        // Switch to the audio-download phase. Progress goes back to 0 and
        // will be driven by wavesurfer's 'loading' event below.
        setDownloadProgress(0);
        setLoadPhase("audio");

        
        // const random = (min, max) => Math.random() * (max - min) + min
        // const randomColor = () => `rgba(${random(0, 255)}, ${random(0, 255)}, ${random(0, 255)}, 0.5)`
        // Initial regions on decode
        wavesurferRef.current.on('decode', () => {
          rebuildRegions(headingsRef.current);
        });
        
        // Track audio-buffer download progress (0-100) so the loading overlay
        // shows real progress, not a stuck 0% bar.
        wavesurferRef.current.on("loading", (percent) => {
          if (Number.isFinite(percent)) {
            setDownloadProgress(Math.max(0, Math.min(100, percent)));
          }
        });

        wavesurferRef.current.on("ready", () => {
          const audioDuration = wavesurferRef.current.getDuration();
          setDuration(audioDuration);
          setIsAudioReady(true);
          setDownloadProgress(null);
          setLoadPhase(null);
          rebuildRegions(headingsRef.current);
        });

        wavesurferRef.current.on("error", (err) => {
          console.error("WaveSurfer error:", err);
          setAudioLoadError(true);
          setDownloadProgress(null);
          setLoadPhase(null);
        });

        wavesurferRef.current.on("audioprocess", () => {
          if (!isSeeking.current) {
            reportCurrentTime(wavesurferRef.current.getCurrentTime());
          }
        });

        wavesurferRef.current.on("interaction", () => {
          reportCurrentTime(wavesurferRef.current.getCurrentTime());
        });

        wavesurferRef.current.on("timeupdate", () => {
          if (isSeeking.current) {
            reportCurrentTime(wavesurferRef.current.getCurrentTime());
          }
        });

        wavesurferRef.current.on("seeking", () => {
          isSeeking.current = true;
        });

        wavesurferRef.current.on("seeked", () => {
          isSeeking.current = false;
          reportCurrentTime(wavesurferRef.current.getCurrentTime());
        });

        wavesurferRef.current.on("play", () => {
          setIsPlaying(true);
          onPlayingStateChange?.(true);
        });
        wavesurferRef.current.on("pause", () => {
          setIsPlaying(false);
          onPlayingStateChange?.(false);
        });

        // --- Custom fine scrubbing handlers (pointer drag) ---
        const el = waveformRef.current;
        if (el) {
          const onPointerDown = (ev) => {
            // start manual scrub
            manualScrub.current = true;
            isSeeking.current = true;
            scrubStartX.current = ev.clientX;
            scrubStartTime.current = wavesurferRef.current.getCurrentTime() || 0;
            el.setPointerCapture(ev.pointerId);
          };

          const onPointerMove = (ev) => {
            if (!manualScrub.current || !wavesurferRef.current) return;
            const rect = el.getBoundingClientRect();
            const deltaX = ev.clientX - scrubStartX.current;
            const width = rect.width || 1;
            const dur = wavesurferRef.current.getDuration() || duration || 0;
            // apply sensitivity to make scrubbing finer
            const deltaTime = (deltaX / width) * dur * SCRUB_SENSITIVITY;
            let newTime = scrubStartTime.current + deltaTime;
            if (newTime < 0) newTime = 0;
            if (newTime > dur) newTime = dur;
            wavesurferRef.current.setTime(newTime);
            reportCurrentTime(newTime);
          };

          const onPointerUp = (ev) => {
            if (!manualScrub.current) return;
            manualScrub.current = false;
            isSeeking.current = false;
            try { el.releasePointerCapture(ev.pointerId); } catch(e) {}
            // ensure wavesurfer state is synced
            const cur = wavesurferRef.current.getCurrentTime();
            reportCurrentTime(cur);
          };

          el.addEventListener('pointerdown', onPointerDown);
          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', onPointerUp);

          // cleanup when wavesurfer destroyed or component unmounts
          const cleanupPointer = () => {
            el.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
          };

          wavesurferRef.current._cleanupPointer = cleanupPointer;
        }

      } catch (err) {
        console.error("❌ Error loading audio:", err);
        setDownloadProgress(null);
        setLoadPhase(null);
        setAudioLoadError(true);
      }
    };

    fetchAndLoadBlob();

    return () => {
      try {
        // run any pointer cleanup attached when creating the wavesurfer
        wavesurferRef.current?._cleanupPointer?.();
      } catch (e) {}
      wavesurferRef.current?.destroy();
    };
  }, [filename, reportCurrentTime, rebuildRegions]);

  // Rebuild regions whenever headings change
  useEffect(() => {
    if (!wavesurferRef.current || !regionsRef.current) return;
    // debounce small changes
    const id = setTimeout(() => {
      rebuildRegions(headingsRef.current);
    }, 50);
    return () => clearTimeout(id);
  }, [headings, rebuildRegions]);

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || newTimeSetted == null) return;
    // Bail out cleanly if audio isn't ready yet — setTime/play before the
    // 'ready' event silently no-ops or throws on some browsers.
    if (!isAudioReady) return;

    const requested = Number(newTimeSetted);
    if (!Number.isFinite(requested)) return;
    if (
      lastExternalJumpRef.current != null &&
      Math.abs(lastExternalJumpRef.current - requested) < 0.01
    ) {
      return;
    }
    lastExternalJumpRef.current = requested;

    clearPendingResumeWait();

    const dur = ws.getDuration() || duration || 0;
    const target = Math.max(0, Math.min(requested, dur || Number.MAX_SAFE_INTEGER));
    const wasPlaying = Boolean(isPlaying || ws.isPlaying?.());

    if (wasPlaying) {
      try {
        ws.pause();
      } catch {}
    }

    ws.setTime(target);
    reportCurrentTime(target);

    const mediaEl = ws.getMediaElement?.();

    // If the target is already buffered, no waiting needed — resume play
    // immediately if the user was playing, otherwise we're done.
    if (isBufferedAtTime(mediaEl, target)) {
      if (wasPlaying) {
        try { ws.play(); } catch {}
      }
      return;
    }

    // Target is not buffered. Show the loading overlay/spinner and wait for
    // the browser to fetch the segment around `target`. We do this even when
    // paused — the user expects feedback that "I clicked, something's
    // loading" regardless of play state.
    setIsSeekBuffering(true);

    const tryResume = () => {
      const currentWs = wavesurferRef.current;
      if (!currentWs) return false;
      if (!isBufferedAtTime(mediaEl, target)) return false;
      if (wasPlaying) {
        try {
          currentWs.play();
        } catch {
          return false;
        }
      }
      return true;
    };

    let timeoutId = null;
    const cleanup = () => {
      if (mediaEl) {
        mediaEl.removeEventListener("canplay", onMediaEvent);
        mediaEl.removeEventListener("canplaythrough", onMediaEvent);
        mediaEl.removeEventListener("progress", onMediaEvent);
        mediaEl.removeEventListener("loadeddata", onMediaEvent);
      }
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
      setIsSeekBuffering(false);
      if (resumeWaitCleanupRef.current === cleanup) {
        resumeWaitCleanupRef.current = null;
      }
    };

    const onMediaEvent = () => {
      if (tryResume()) {
        cleanup();
      }
    };

    if (mediaEl) {
      mediaEl.addEventListener("canplay", onMediaEvent);
      mediaEl.addEventListener("canplaythrough", onMediaEvent);
      mediaEl.addEventListener("progress", onMediaEvent);
      mediaEl.addEventListener("loadeddata", onMediaEvent);
    }

    // Fallback: after a longer wait, try to resume even if buffered events
    // are sparse. 8s instead of the previous 3.5s — far seeks on big files
    // (45-min plenary sessions, ~40MB MP3) routinely need more than 3.5s.
    timeoutId = setTimeout(() => {
      tryResume();
      cleanup();
    }, 8000);

    resumeWaitCleanupRef.current = cleanup;

    return cleanup;
  }, [
    newTimeSetted,
    reportCurrentTime,
    duration,
    isPlaying,
    isAudioReady,
    clearPendingResumeWait,
    isBufferedAtTime,
  ]);

  const togglePlay = () => {
    if (!wavesurferRef.current) return;
    
    // Check if audio is ready before trying to play/pause
    try {
      const duration = wavesurferRef.current.getDuration();
      if (!duration || duration === 0 || !isFinite(duration)) {
        console.warn('Audio not ready yet');
        return;
      }
      wavesurferRef.current.playPause();
    } catch (error) {
      console.error('Error toggling play:', error);
    }
  };

  const stopAudio = useCallback(() => {
    wavesurferRef.current?.stop();
    setIsPlaying(false);
    reportCurrentTime(0);
  }, [reportCurrentTime]);

  // Step seek helpers
  const stepSeek = (seconds) => {
    if (!wavesurferRef.current) return;
    const dur = wavesurferRef.current.getDuration() || duration || 0;
    let target = (wavesurferRef.current.getCurrentTime() || 0) + seconds;
    if (target < 0) target = 0;
    if (target > dur) target = dur;
    wavesurferRef.current.setTime(target);
    reportCurrentTime(target);
  };
  const jumpToExactTime = (target) => {
    if (!wavesurferRef.current || !Number.isFinite(target)) return;
    const dur = wavesurferRef.current.getDuration() || duration || 0;
    const clamped = Math.max(0, Math.min(target, dur));
    wavesurferRef.current.setTime(clamped);
    reportCurrentTime(clamped);
  };
  const moveToAdjacentWord = (direction) => {
    if (!normalizedWordOffsets.length || !wavesurferRef.current) return;
    const epsilon = 0.01;
    const current = wavesurferRef.current.getCurrentTime() || 0;
    if (direction > 0) {
      const next = normalizedWordOffsets.find((time) => time > current + epsilon);
      if (next != null) jumpToExactTime(next);
    } else if (direction < 0) {
      let previous = null;
      for (const time of normalizedWordOffsets) {
        if (time >= current - epsilon) break;
        previous = time;
      }
      if (previous != null) jumpToExactTime(previous);
    }
  };
  const handlePreviousWord = () => moveToAdjacentWord(-1);
  const handleNextWord = () => moveToAdjacentWord(1);

  // Normalize speaker/paragraph start times (similar to word offsets)
  const normalizedSpeakerStarts = useMemo(() => {
    if (!Array.isArray(paragraphs) || paragraphs.length === 0) return [];
    const sanitized = paragraphs
      .map((p) => (p?.start != null && Number.isFinite(p.start) ? p.start : null))
      .filter((value) => value != null)
      .sort((a, b) => a - b);
    const deduped = [];
    for (const value of sanitized) {
      if (deduped.length === 0 || Math.abs(deduped[deduped.length - 1] - value) > 0.01) {
        deduped.push(value);
      }
    }
    return deduped;
  }, [paragraphs]);

  // Navigate between speakers/sections
  const moveToAdjacentSpeaker = (direction) => {
    if (!normalizedSpeakerStarts.length || !wavesurferRef.current) return;
    const current = wavesurferRef.current.getCurrentTime() || 0;
    if (direction > 0) {
      // Next speaker (down arrow)
      const next = normalizedSpeakerStarts.find((time) => time > current + 0.01);
      if (next != null) jumpToExactTime(next);
    } else if (direction < 0) {
      // Previous speaker (up arrow)
      // Find the index of current speaker section
      let currentSpeakerIndex = -1;
      for (let i = 0; i < normalizedSpeakerStarts.length; i++) {
        if (normalizedSpeakerStarts[i] <= current) {
          currentSpeakerIndex = i;
        } else {
          break;
        }
      }
      // Jump to the previous speaker (one before current)
      if (currentSpeakerIndex > 0) {
        jumpToExactTime(normalizedSpeakerStarts[currentSpeakerIndex - 1]);
      }
    }
  };
  const handlePreviousSpeaker = () => moveToAdjacentSpeaker(-1);
  const handleNextSpeaker = () => moveToAdjacentSpeaker(1);

  // Keyboard shortcuts (Ctrl on Win/Linux, Option or Ctrl on macOS)
  useEffect(() => {
    const onKey = (e) => {
      // Don't allow keyboard shortcuts if audio is not ready
      if (!isAudioReady) return;

      // Require expected modifier to avoid accidental triggers.
      const hasModifier = isMacOS ? (e.altKey || e.ctrlKey) : e.ctrlKey;
      if (!hasModifier) return;

      // Speed controls: Modifier+Shift+Arrow Up/Down
      if (e.shiftKey && e.code === 'ArrowUp') {
        e.preventDefault();
        stepSpeed(1);
        return;
      }
      if (e.shiftKey && e.code === 'ArrowDown') {
        e.preventDefault();
        stepSpeed(-1);
        return;
      }

      // Stop control: Modifier+Shift+Space
      if (e.shiftKey && e.code === 'Space') {
        e.preventDefault();
        stopAudio();
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
        return;
      }

      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        moveToAdjacentWord(-1);
        return;
      }

      if (e.code === 'ArrowRight') {
        e.preventDefault();
        moveToAdjacentWord(1);
        return;
      }

      if (e.code === 'ArrowUp') {
        e.preventDefault();
        moveToAdjacentSpeaker(-1);
        return;
      }

      if (e.code === 'ArrowDown') {
        e.preventDefault();
        moveToAdjacentSpeaker(1);
      }
    };

    // Capture phase ensures shortcuts still work even if inner components stop propagation.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [
    isAudioReady,
    isMacOS,
    togglePlay,
    stopAudio,
    moveToAdjacentWord,
    moveToAdjacentSpeaker,
    stepSpeed,
  ]);

  // Pause audio when tab becomes inactive
  useEffect(() => {
    if (!isActive && wavesurferRef.current && isPlaying) {
      try {
        wavesurferRef.current.pause();
        setIsPlaying(false);
        onPlayingStateChange?.(false);
      } catch (error) {
        console.error('Error pausing audio on tab switch:', error);
      }
    }
  }, [isActive, isPlaying, onPlayingStateChange]);

  const wordNavDisabled = !isAudioReady || normalizedWordOffsets.length === 0;
  const speakerNavDisabled = !isAudioReady || normalizedSpeakerStarts.length === 0;

  return (
    <div className="flex flex-col items-center w-full py-2 px-6 bg-surface rounded-full relative">
      <div className="flex items-center gap-4 w-full">
        {/* Button Group */}
        <div className="flex gap-2 items-center">
          <AppButton 
            onClick={cycleSpeed} 
            width="w-12" 
            rounded="rounded-[16px]"
            disabled={!isAudioReady}
            className={`p-1.5 text-sm ${!isAudioReady ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Playback speed"
            title={`Playback speed (${modifierLabel}+Shift+Arrow Up/Down)`}
          >
            {playbackRate}x
          </AppButton>
          <AppButton
            onClick={togglePlay}
            width="w-12"
            rounded="rounded-[16px]"
            disabled={!isAudioReady || isSeekBuffering}
            className={`p-1.5 ${!isAudioReady || isSeekBuffering ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={
              !isAudioReady
                ? "Audio wird geladen…"
                : isSeekBuffering
                  ? "Audio-Position wird geladen…"
                  : isPlaying
                    ? `Pause (${modifierLabel}+Space)`
                    : `Play (${modifierLabel}+Space)`
            }
            title={
              !isAudioReady
                ? "Audio wird geladen…"
                : isSeekBuffering
                  ? "Audio-Position wird geladen…"
                  : isPlaying
                    ? `Pause (${modifierLabel}+Space)`
                    : `Play (${modifierLabel}+Space)`
            }
          >
            {!isAudioReady || isSeekBuffering ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isPlaying ? (
              <PauseIcon />
            ) : (
              <PlayIcon />
            )}
          </AppButton>
          <AppButton 
            onClick={stopAudio} 
            width="w-12" 
            rounded="rounded-[16px]"
            disabled={!isAudioReady}
            className={`p-1.5 ${!isAudioReady ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={`Stop (${modifierLabel}+Shift+Space)`}
            title={`Stop (${modifierLabel}+Shift+Space)`}
          >
            <StopIcon />
          </AppButton>
          <AppButton
            onClick={handlePreviousSpeaker}
            width="w-12"
            rounded="rounded-[16px]"
            disabled={speakerNavDisabled}
            className={`p-1.5 ${speakerNavDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label={`Previous speaker (${modifierLabel}+Arrow Up)`}
            title={`Previous speaker (${modifierLabel}+Arrow Up)`}
          >
            <ChevronUp className="w-4 h-4" />
          </AppButton>
          <AppButton
            onClick={handleNextSpeaker}
            width="w-12"
            rounded="rounded-[16px]"
            disabled={speakerNavDisabled}
            className={`p-1.5 ${speakerNavDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label={`Next speaker (${modifierLabel}+Arrow Down)`}
            title={`Next speaker (${modifierLabel}+Arrow Down)`}
          >
            <ChevronDown className="w-4 h-4" />
          </AppButton>
          <AppButton
            onClick={handlePreviousWord}
            width="w-12"
            rounded="rounded-[16px]"
            disabled={wordNavDisabled}
            className={`p-1.5 ${wordNavDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label={`Previous word (${modifierLabel}+Arrow Left)`}
            title={`Previous word (${modifierLabel}+Arrow Left)`}
          >
            <ChevronLeft className="w-4 h-4" />
          </AppButton>
          <AppButton
            onClick={handleNextWord}
            width="w-12"
            rounded="rounded-[16px]"
            disabled={wordNavDisabled}
            className={`p-1.5 ${wordNavDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label={`Next word (${modifierLabel}+Arrow Right)`}
            title={`Next word (${modifierLabel}+Arrow Right)`}
          >
            <ChevronRight className="w-4 h-4" />
          </AppButton>
        </div>
  
        {/* Waveform & Time */}
        <div className="flex-1 flex items-center gap-4">
          <div className="flex-1 relative h-[50px] overflow-hidden rounded-full border-2">
            {/* Loading overlay — shown until wavesurfer fires 'ready', OR
                while we're waiting on the browser to buffer a far-seek
                position the user clicked. The waveform itself stays mounted
                underneath so wavesurfer can attach to its container — we
                just cover it with the spinner. */}
            {filename &&
              (!isAudioReady || isSeekBuffering) &&
              !audioLoadError && (
                <>
                  <div className="absolute left-0 top-0 w-full h-full z-[15] overflow-hidden rounded-full">
                    <div
                      className={`w-full h-full ${isSeekBuffering && isAudioReady ? "bg-surface/80 backdrop-blur-sm" : "bg-gray-200"} rounded-full`}
                    >
                      {!isSeekBuffering && (
                        <div
                          className="bg-secondaryContainer h-full rounded-full transition-all duration-200"
                          style={{ width: `${downloadProgress ?? 0}%` }}
                        ></div>
                      )}
                    </div>
                  </div>
                  <div className="absolute left-0 top-0 w-full h-full z-[16] flex items-center justify-center gap-2 pointer-events-none">
                    <Loader2 className="w-4 h-4 animate-spin text-default-600" />
                    <span className="text-sm font-medium text-default-600 px-1">
                      {isSeekBuffering
                        ? "Audio-Position wird geladen…"
                        : loadPhase === "peaks"
                          ? "Audiopegel generieren…"
                          : "Audio wird geladen…"}
                      {!isSeekBuffering &&
                        downloadProgress != null &&
                        loadPhase === "audio" && (
                          <span className="ml-1 text-default-500">
                            {Math.round(downloadProgress)}%
                          </span>
                        )}
                    </span>
                  </div>
                </>
              )}

            {/* Error overlay */}
            {filename && audioLoadError && (
              <div className="absolute left-0 top-0 w-full h-full z-[16] flex items-center justify-center pointer-events-none bg-surface/80">
                <span className="text-sm font-medium text-error px-2 py-1">
                  Audio konnte nicht geladen werden
                </span>
              </div>
            )}

            {/* Waveform */}
            <div
              ref={waveformRef}
              className="absolute left-0 top-0 w-full h-full z-10 rounded-full"
              style={{ willChange: 'transform', contain: 'strict', visibility: isAudioReady ? 'visible' : 'hidden' }}
            ></div>

            {/* Highlight ticks — colored bands over the waveform marking
                each saved highlight's time range. Click to jump. */}
            {isAudioReady && duration > 0 && Array.isArray(highlights) && highlights.length > 0 && (
              <div className="absolute left-0 top-0 w-full h-full z-[11] pointer-events-none rounded-full overflow-hidden">
                {highlights.map((h) => {
                  const start = Number(h.start_offset);
                  const end = Number(h.end_offset);
                  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
                  const leftPct = Math.max(0, Math.min(100, (start / duration) * 100));
                  // Minimum tick width of 0.5% so a single-word highlight is still visible.
                  const rawWidthPct = ((end - start) / duration) * 100;
                  const widthPct = Math.max(0.5, Math.min(100 - leftPct, rawWidthPct));
                  return (
                    <button
                      key={`tick-${h.id}`}
                      type="button"
                      onClick={() => onHighlightClick?.(h)}
                      title={h.snippet || "Hervorhebung"}
                      className="absolute top-0 h-full pointer-events-auto cursor-pointer transition-opacity hover:opacity-100"
                      style={{
                        left: `${leftPct}%`,
                        width: `${widthPct}%`,
                        backgroundColor: h.color,
                        opacity: 0.55,
                        border: 0,
                        padding: 0,
                      }}
                      aria-label={`Zu Hervorhebung springen: ${h.snippet || ""}`}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Autoscroll Switch */}
          {onAutoscrollChange && (
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-onSurface/70 whitespace-nowrap">Autoscroll</span>
              <Switch
                isSelected={autoscrollEnabled}
                onValueChange={onAutoscrollChange}
                size="sm"
                aria-label="Autoscroll"
                classNames={{
                  wrapper: "group-data-[selected=true]:bg-secondaryContainer"
                }}
              />
            </div>
          )}

          {/* Undo — hidden until UNDO_ENABLED */}
          {UNDO_ENABLED && <UndoButton sessionId={sessionId} />}

          {/* 3-dot menu + Search dropdown + Time */}
          <div className="flex items-center gap-2">
            {/* 3-dot overflow menu */}
            <Dropdown className="text-onSurface bg-surfaceContainer p-0 border-0">
              <DropdownTrigger>
                <Button isIconOnly className="h-8 w-8 hover:bg-secondaryContainer bg-secondaryContainer rounded-[16px]">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                className="p-0"
                aria-label="Audio player actions"
                onAction={(key) => {
                  if (key === "export") {
                    onExportClick?.();
                  } else if (key === "search") {
                    setIsSearchOpen((prev) => !prev);
                  } else if (key === "speakers") {
                    onToggleSpeakersModal?.();
                  } else if (key === "hard" && promptMode !== "hard") {
                    onPromptModeChange?.("hard");
                  } else if (key === "soft" && promptMode !== "soft") {
                    onPromptModeChange?.("soft");
                  }
                }}
              >
                {onToggleSpeakersModal && (
                  <DropdownItem
                    key="speakers"
                    startContent={<Users className="w-4 h-4" />}
                    className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                    textValue="Sprecher verwalten"
                  >
                    Sprecher verwalten
                  </DropdownItem>
                )}
                {onExportClick && (
                  <DropdownItem
                    key="export"
                    startContent={<FolderOutput className="w-4 h-4" />}
                    className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                    textValue="Export"
                  >
                    Export
                  </DropdownItem>
                )}
                <DropdownItem
                  key="search"
                  startContent={<Search className="w-4 h-4" />}
                  className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                  textValue={isSearchOpen ? "Suche ausblenden" : "Suche anzeigen"}
                >
                  {isSearchOpen ? "Suche ausblenden" : "Suche anzeigen"}
                </DropdownItem>
                {onPromptModeChange && (
                  <>
                    <DropdownItem
                      key="hard"
                      className={`text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer ${
                        promptMode === "hard" ? "bg-secondaryContainer" : ""
                      }`}
                      textValue="Hard"
                    >
                      Hard
                    </DropdownItem>
                    <DropdownItem
                      key="soft"
                      className={`text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer ${
                        promptMode === "soft" ? "bg-secondaryContainer" : ""
                      }`}
                      textValue="Soft"
                    >
                      Soft
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            </Dropdown>
            
            {/* Search dropdown (conditionally shown when opened from menu) */}
            {isSearchOpen && (
              <div className="relative" ref={searchToggleRef}>
                <div
                  ref={searchDropdownRef}
                  className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-outlineVariant bg-surface p-3 shadow-lg z-20"
                >
                  <Input
                    value={searchQuery}
                    onChange={(e) => onSearchQueryChange?.(e.target.value)}
                    placeholder="Text suchen..."
                    size="sm"
                    className="w-full"
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        onSearchNext?.();
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        onSearchPrev?.();
                      }
                    }}
                  />
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <div className="flex items-center gap-2">
                      <AppButton
                        width="w-10"
                        roundedFull="rounded-full"
                        className="p-1"
                        onClick={onSearchPrev}
                        aria-label="Previous match"
                        title="Previous match"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </AppButton>
                      <AppButton
                        width="w-10"
                        roundedFull="rounded-full"
                        className="p-1"
                        onClick={onSearchNext}
                        aria-label="Next match"
                        title="Next match"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </AppButton>
                    </div>
                    <span className="text-xs text-onSurface/70">
                      {searchPosition?.total
                        ? `${searchPosition.index + 1} / ${searchPosition.total}`
                        : "0 / 0"}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="pe-0 text-base text-default-400 whitespace-nowrap w-[100px] text-end">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  
};

export default AudioPlayer;
